import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { socialApi, type SocialAnalytics } from "@/api/social";

function StatCard({ label, value }: { label: string; value: string | number }) {
	return (
		<Card>
			<CardHeader className="pb-2">
				<CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
			</CardHeader>
			<CardContent>
				<div className="text-2xl font-semibold">{value}</div>
			</CardContent>
		</Card>
	);
}

export default function SocialAnalyticsPage() {
	const { data, isLoading, refetch, isFetching } = useQuery<SocialAnalytics>({
		queryKey: ["social-analytics"],
		queryFn: () => socialApi.analytics(),
		refetchInterval: 60_000,
	});

	return (
		<div className="space-y-6 p-4 md:p-6">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<h1 className="text-2xl font-semibold">Instagram organic analytics</h1>
					<p className="mt-1 text-sm text-muted-foreground">
						Classification mix, media volume, and CRM lead counts (Phase 1).
					</p>
				</div>
				<div className="flex gap-3 text-sm">
					<button
						type="button"
						className="underline-offset-4 hover:underline"
						onClick={() => refetch()}
						disabled={isFetching}
					>
						{isFetching ? "Refreshing…" : "Refresh"}
					</button>
					<Link className="underline-offset-4 hover:underline" to="/hospital-admin/social/inbox">
						Inbox
					</Link>
				</div>
			</div>

			{isLoading ? (
				<p className="text-sm text-muted-foreground">Loading…</p>
			) : (
				<>
					<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
						<StatCard label="Organic CRM leads" value={data?.organicLeadCount ?? 0} />
						<StatCard label="CRM-linked social rows" value={data?.crmLinkedSocialLeads ?? 0} />
						<StatCard
							label="Lead classifications"
							value={
								data?.byClassification?.find((x) => x.classification === "LEAD")?.count ?? 0
							}
						/>
						<StatCard
							label="Maybe / review"
							value={
								data?.byClassification?.find((x) => x.classification === "MAYBE_LEAD")
									?.count ?? 0
							}
						/>
					</div>

					<div className="grid gap-4 md:grid-cols-2">
						<Card>
							<CardHeader>
								<CardTitle className="text-base">By classification</CardTitle>
							</CardHeader>
							<CardContent className="space-y-2 text-sm">
								{(data?.byClassification ?? []).map((r) => (
									<div key={r.classification} className="flex justify-between">
										<span>{r.classification.replace("_", " ")}</span>
										<span className="tabular-nums">{r.count}</span>
									</div>
								))}
								{(data?.byClassification ?? []).length === 0 ? (
									<p className="text-muted-foreground">No data yet.</p>
								) : null}
							</CardContent>
						</Card>
						<Card>
							<CardHeader>
								<CardTitle className="text-base">Conversation status</CardTitle>
							</CardHeader>
							<CardContent className="space-y-2 text-sm">
								{(data?.conversationByStatus ?? []).map((r) => (
									<div key={r.status} className="flex justify-between">
										<span>{r.status}</span>
										<span className="tabular-nums">{r.count}</span>
									</div>
								))}
							</CardContent>
						</Card>
						<Card>
							<CardHeader>
								<CardTitle className="text-base">Top procedures</CardTitle>
							</CardHeader>
							<CardContent className="space-y-2 text-sm">
								{(data?.topProcedures ?? []).map((r) => (
									<div key={String(r.procedure)} className="flex justify-between">
										<span>{r.procedure || "Unknown"}</span>
										<span className="tabular-nums">{r.count}</span>
									</div>
								))}
							</CardContent>
						</Card>
						<Card>
							<CardHeader>
								<CardTitle className="text-base">By media (post/reel id)</CardTitle>
							</CardHeader>
							<CardContent className="space-y-2 text-sm">
								{(data?.byMedia ?? []).map((r) => (
									<div key={String(r.mediaId)} className="flex justify-between gap-2">
										<span className="truncate font-mono text-xs">{r.mediaId}</span>
										<span className="tabular-nums">{r.count}</span>
									</div>
								))}
							</CardContent>
						</Card>
					</div>
				</>
			)}
		</div>
	);
}
